import React, { useState, useEffect } from 'react';
import io from 'socket.io-client';
import './App.css';
import Home from './components/Home';
import Lobby from './components/Lobby';
import Game from './components/Game';
import CardTest from './components/CardTest';

const SOCKET_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:4600' 
  : 'https://www.partygamez.com';

function App() {
  const [socket, setSocket] = useState(null);
  const [screen, setScreen] = useState(window.location.pathname === '/card' ? 'card' : 'home');
  const [playerName, setPlayerName] = useState('');
  const [lobbyCode, setLobbyCode] = useState('');
  const [lobby, setLobby] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [language, setLanguage] = useState(localStorage.getItem('rtb_language') || 'en');

  useEffect(() => {
    localStorage.setItem('rtb_language', language);
  }, [language]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get('code');
    const urlName = params.get('name');
    if (urlCode) {
      setLobbyCode(urlCode.toUpperCase());
      if (urlName) {
        setPlayerName(decodeURIComponent(urlName));
      }
      window.history.replaceState({}, '', window.location.pathname);
    }

    const newSocket = io(SOCKET_URL, {
      path: '/rtb-api/socket.io/'
    });
    setSocket(newSocket);

    // Check for saved session
    const savedSession = sessionStorage.getItem('rtb_session');
    if (savedSession) {
      try {
        const { code, name } = JSON.parse(savedSession);
        if (!urlCode) setLobbyCode(code);
        if (!urlName) setPlayerName(name);
      } catch (e) {
        sessionStorage.removeItem('rtb_session');
      }
    }

    newSocket.on('lobbyCreated', ({ code, lobby }) => {
      setLobbyCode(code);
      setLobby(lobby);
      setScreen('lobby');
      sessionStorage.setItem('rtb_session', JSON.stringify({ code, name: playerName }));
    });

    newSocket.on('lobbyUpdate', (updatedLobby) => {
      console.log('App.js: lobbyUpdate received:', updatedLobby);
      console.log('App.js: Players multipliers:', updatedLobby.players.map(p => ({ name: p.name, multiplier: p.multiplier })));
      // Force re-render by creating new object reference
      setLobby({ ...updatedLobby, players: [...updatedLobby.players] });
      // If game returned to lobby state, switch screen back
      if (updatedLobby.gameState === 'lobby') {
        setScreen('lobby');
      }
      // Save session when joining
      if (lobbyCode && playerName) {
        sessionStorage.setItem('rtb_session', JSON.stringify({ code: lobbyCode, name: playerName }));
      }
    });

    newSocket.on('error', ({ message }) => {
      if (message && message.includes('Lobby not found')) {
        sessionStorage.removeItem('rtb_session');
        setLobbyCode('');
        setPlayerName('');
      }
      alert(message);
    });

    newSocket.on('kicked', () => {
      alert('You have been kicked from the lobby');
      setScreen('home');
      setLobby(null);
      setLobbyCode('');
    });

    newSocket.on('gameStarted', (updatedLobby) => {
      setLobby(updatedLobby);
      setScreen('game');
    });

    return () => newSocket.close();
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return (
    <div className="App">
      {screen === 'card' && <CardTest />}
      {screen === 'home' && (
        <Home
          socket={socket}
          playerName={playerName}
          setPlayerName={setPlayerName}
          setScreen={setScreen}
          lobbyCode={lobbyCode}
          setLobbyCode={setLobbyCode}
          setLobby={setLobby}
          language={language}
          setLanguage={setLanguage}
          savedSession={sessionStorage.getItem('rtb_session') ? JSON.parse(sessionStorage.getItem('rtb_session')) : null}
        />
      )}
      {screen === 'lobby' && (
        <Lobby
          socket={socket}
          lobby={lobby}
          lobbyCode={lobbyCode}
          playerName={playerName}
          toggleFullscreen={toggleFullscreen}
          isFullscreen={isFullscreen}
          onLeave={() => {
            sessionStorage.removeItem('rtb_session');
            setScreen('home');
            setLobby(null);
            setLobbyCode('');
          }}
        />
      )}
      {screen === 'game' && (
        <Game
          socket={socket}
          lobby={lobby}
          lobbyCode={lobbyCode}
          playerName={playerName}
          toggleFullscreen={toggleFullscreen}
          isFullscreen={isFullscreen}
        />
      )}
    </div>
  );
}

export default App;
